/**
 * Encounter respawn — session-tick cut (HEX-94 / ADR-0012 §2).
 *
 * ADR-0012 §2 parked the respawn-cadence curve ("time-based, traffic-based, or
 * session-tick-based") pending combat playtest. This is the cheapest honest cut
 * of that intent: a *session tick*. A resolved encounter slain in a PREVIOUS
 * session (or by a previous character) rolls a respawn chance each time the
 * world reads it; on a hit the tile returns to a live encounter so trodden
 * regions stay alive on return trips. No real-time clocks, no slayer-buff
 * (that half of §2 is a separate ticket).
 *
 * SAME-SESSION GATE (no farming within a run). The roll only fires on a tile
 * resolved *before this session booted* — `resolvedAt < sessionBootAt`. A tile
 * slain during the current run has `resolvedAt >= sessionBootAt` and never
 * respawns, so a player cannot grind the same hex over and over in one sitting.
 * `sessionBootAt` is a wall-clock stamp captured ONCE at session start; it is a
 * comparison gate, NOT an input to the deterministic roll (see below) — the
 * ticket's "no Date.now in the roll path" constraint holds.
 *
 * DETERMINISM (ADR-0013 PRNG discipline). The respawn roll and the fresh enemy
 * pick are seeded purely from `(q, r, worldSeed, crossings)` — no wall-clock.
 * `tile.crossings` is the per-cycle nonce (the same footfall counter the combat
 * seed folds in, HEX-87): it bumps on every cross before the world reads the
 * tile, so a re-cross after a miss rolls a fresh chance and a successful respawn
 * re-picks from a fresh stream rather than replaying the slain enemy. Given the
 * same seed + tile + crossings the outcome is reproducible.
 *
 * FRESH TEMPLATE (ADR-0012 §2 "the same encounter identity respawns" is the
 * eventual slayer-buff lock; this cut re-picks from the tier band for the
 * tile's distance — a fresh draw, not necessarily the same enemy). The
 * within-tier roll is decorrelated from the resolution so a respawn can swap a
 * Goblin Striker for a Cave Spider at the same hex.
 */

import { createRng } from '../combat/dice';
import type { Tile } from '../core/tile';
import { pickLatentEnemy, safeZoneDistance } from './frontierRisk';

/**
 * Respawn probability for an eligible (previous-session, resolved) encounter.
 * Tunable first-pass (ADR-0012 §2 curve deliberately unpinned): ~50% per read
 * so a returning player meets a roughly even mix of cleared remnants and
 * re-stocked hexes. Raise to make the world re-populate faster, lower for more
 * lasting clears.
 */
export const ENCOUNTER_RESPAWN_CHANCE = 0.5;

/** Salt so the respawn seed decorrelates from the combat seed at the same tile. */
const RESPAWN_SALT = 0x5eed_94 | 0;

export interface RespawnOptions {
  /**
   * Wall-clock stamp captured ONCE at session boot. The same-session gate
   * compares `resolvedAt < sessionBootAt`; it is never folded into the roll.
   */
  readonly sessionBootAt: number;
  /** World generation seed — folded into the deterministic roll + enemy pick. */
  readonly worldSeed: number;
}

/**
 * True when `tile` is a resolved encounter slain in a previous session — the
 * only state eligible for a respawn roll. A same-session kill
 * (`resolvedAt >= sessionBootAt`), a never-resolved encounter
 * (`resolvedAt === null` — including a defeat where the player fell), or any
 * non-encounter tile is ineligible.
 */
export function isRespawnEligible(tile: Tile, sessionBootAt: number): boolean {
  return (
    tile.type === 'encounter' &&
    tile.state === 'resolved' &&
    tile.lifecycleState !== null &&
    tile.lifecycleState.kind === 'encounter' &&
    tile.lifecycleState.resolvedAt !== null &&
    tile.lifecycleState.resolvedAt < sessionBootAt
  );
}

/**
 * Deterministic respawn seed from tile identity + world seed + footfall nonce.
 * Mirrors the `combatSeedFor` discipline (HEX-87): no wall-clock, `crossings`
 * as the per-cycle nonce so each re-cross draws a fresh stream.
 */
function respawnSeed(tile: Tile, worldSeed: number): number {
  return mix(tile.q, tile.r, worldSeed, tile.crossings, RESPAWN_SALT);
}

/**
 * Roll a respawn for a resolved encounter. Mutates `tile` IN PLACE and returns
 * `true` when the encounter came back to life (caller persists); returns `false`
 * — and leaves the tile untouched — when the tile is ineligible or the roll
 * misses.
 *
 * On a hit the tile:
 *   - re-picks a fresh enemy from the tier band for its safe-zone distance
 *     (`pickLatentEnemy`), which may differ from the slain one;
 *   - clears `resolvedAt` (back to `null`) so the `†` death-mark remnant clears
 *     and `isSlainEncounter` reads it as live again;
 *   - flips `state` back to `revealed` so `buildEncounter` no longer
 *     short-circuits on the resolved guard.
 *
 * `slayerBuffStack` is preserved (the tile's lived-experience overlay survives
 * a respawn; the buff *behaviour* is a separate §2 ticket).
 */
export function maybeRespawnEncounter(tile: Tile, opts: RespawnOptions): boolean {
  if (!isRespawnEligible(tile, opts.sessionBootAt)) return false;
  const state = tile.lifecycleState;
  // Narrowed by isRespawnEligible, but TS needs the in-scope guard.
  if (state === null || state.kind !== 'encounter') return false;

  const rng = createRng(respawnSeed(tile, opts.worldSeed));
  // First draw: the respawn gate. Miss → tile stays a cleared remnant.
  if (rng() >= ENCOUNTER_RESPAWN_CHANCE) return false;

  // Hit: re-pick from the tier band for this tile's distance. `pickLatentEnemy`
  // consumes two draws (tier roll + within-tier pick) on the same stream.
  const d = safeZoneDistance(tile.q, tile.r, opts.worldSeed);
  const enemy = pickLatentEnemy(rng, d);

  tile.lifecycleState = {
    ...state,
    enemyId: enemy.id,
    resolvedAt: null,
  };
  tile.state = 'revealed';
  return true;
}

// FNV-ish small integer hash, mirrors `combat/encounter.ts` + `ChunkedWorldGen`
// so every seed-mixing path in the codebase folds inputs identically.
function mix(...inputs: number[]): number {
  let h = 0x811c9dc5;
  for (const x of inputs) {
    h ^= x | 0;
    h = Math.imul(h, 0x01000193);
  }
  return h | 0;
}
